import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import odooIcon from "assets/odoo-icon.svg";
import Button from "components/base/Button";
import { Input } from "components/base/Form";
import { Alert } from "components/base/Toast";
import OdooProductCategorySelector from "components/shared/OdooProductCategorySelector";
import { CurrencyContext } from "CurrencyProvider";
import { productUnitCost } from "pages/price/utils";
import { useContext, useState } from "react";
import { formatCountry } from "utils/string";
import { ODOO_CREATE_PRODUCT } from "./graphql";
import OdooLocationsMultipleSelector from "./OdooLocationsMultipleSelector";
import SupplierInfoView from "./SupplierInfoView";

const PRODUCT_TYPES = { consu: "Goods", service: "Service" };

const ROUTES = ["Buy", "Manufacture", "Make to Order"];

const SyncErrorHandler = (error) => {
  let message = error.message;
  if (message.includes("A barcode can only be assigned to one product !")) {
    message = "A barcode can only be assigned to one product !";
  } else {
    message = parseError(error);
  }
  window.alert(`Sync to Odoo failed: ${message}`);
};

// This form is used to sync to odoo AB.
const CreateOdooProductForm = ({ id, originalProduct, onCreated }) => {
  let defaultRoutes = ["Make to Order"];
  if (originalProduct.isPurchasable) defaultRoutes.push("Buy");
  if (originalProduct.isSalable > 0) defaultRoutes.push("Manufacture");

  let defaultReorderingRules = [
    { companyId: 1, id: 15 }, // WH-C/Stock
    { companyId: 3, id: 167 }, // Wabob/Stock
  ];
  if (originalProduct.company === "AB")
    defaultReorderingRules = [{ companyId: 1, id: 15 }];
  if (originalProduct.company === "LTD")
    defaultReorderingRules = [{ companyId: 3, id: 167 }];

  const { latestExchangeRate } = useContext(CurrencyContext);
  const [createOdooProduct, createOdooProductRes] = useMutation(
    ODOO_CREATE_PRODUCT,
    {
      onCompleted: (res) => {
        Alert("success", "Sync success.");
        if (onCreated) onCreated(res.odooCreateProduct.product);
      },
      onError: SyncErrorHandler,
    },
  );

  const [categId, setCategId] = useState(
    originalProduct.isPurchasable ? 1 : "0",
  );

  const [createBom, setCreateBom] = useState(false);

  const [productType, setProductType] = useState("consu");

  const [routes, setRoutes] = useState(defaultRoutes);
  const [reorderingRules, setReorderingRules] = useState(
    defaultReorderingRules,
  );

  console.log("originalProduct", originalProduct);

  const [saleOk, setSaleOk] = useState(originalProduct.isSalable);
  const [purchaseOk, setPurchaseOk] = useState(originalProduct.isPurchasable);
  const [barCode, setBarCode] = useState(originalProduct.barCode);
  const [weight, setWeight] = useState(
    (parseFloat(originalProduct.itemWeight) / 1000).toFixed(3),
  );
  const [hsCode, setHsCode] = useState(
    originalProduct.productLine?.hsCodeForEu || "",
  );

  const hasBomProductsNotInOdoo =
    originalProduct.children.filter(
      (i) => i.product && i.product.odooId === null,
    ).length > 0;

  const prices = originalProduct.activePrices;

  const computedCost = productUnitCost(
    originalProduct,
    originalProduct.defaultQty,
    latestExchangeRate.rate,
  );

  function validateInput() {
    if (categId === "0") {
      window.alert(
        "Please select a product category, this is required by Odoo.",
      );
      return false;
    }
    return true;
  }

  function submitHandler() {
    if (!validateInput()) return;
    createOdooProduct({
      variables: {
        id,
        categId,
        productType,
        routes,
        reorderingRules,
        saleOk,
        purchaseOk,
        barCode,
        weight: parseFloat(weight),
        hsCode,
        createBom,
      },
    });
  }

  return (
    <div className="text-sm">
      <div className="card">
        <table className="with-line">
          <tbody>
            <tr>
              <td>
                <label>Company:</label>
              </td>
              <td>
                <b>{originalProduct.company}</b>
              </td>
            </tr>
            <tr>
              <td>
                <label>Name:</label>
              </td>
              <td>
                {originalProduct.name}
                {originalProduct.version && ` | v${originalProduct.version}`}
              </td>
            </tr>
            <tr>
              <td>
                <label>Name CN:</label>
              </td>
              <td>{originalProduct.nameCn}</td>
            </tr>
            <tr>
              <td>
                <label>Item Number:</label>
              </td>
              <td>{originalProduct.number}</td>
            </tr>
            <tr>
              <td>
                <label>Country of Origin:</label>
              </td>
              <td>{formatCountry(originalProduct.countryCodeOfOrigin)}</td>
            </tr>
            <tr>
              <td>
                <label>Weight(kg):</label>
              </td>
              <td>
                <Input
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                />
              </td>
            </tr>
            <tr>
              <td>
                <label>Barcode:</label>
              </td>
              <td>
                <Input
                  className="w-full"
                  value={barCode}
                  onChange={(e) => setBarCode(e.target.value)}
                />
              </td>
            </tr>
            <tr>
              <td>
                <label>HS Code:</label>
              </td>
              <td>
                <Input
                  className="w-full"
                  value={hsCode}
                  onChange={(e) => setHsCode(e.target.value)}
                />
              </td>
            </tr>

            <tr>
              <td className="align-baseline">
                <label>Product Category:</label>
              </td>
              <td className="w-full align-baseline">
                <OdooProductCategorySelector
                  instance="AB"
                  value={categId}
                  onChange={setCategId}
                  company={originalProduct.company}
                />
                <div className="mt-1 mx-1 opacity-70 text-xs">
                  Product categories are managed in Odoo.
                </div>
              </td>
            </tr>
            <tr>
              <td>
                <label>Product Type:</label>
              </td>
              <td className="w-full">
                <div className="flex space-x-6">
                  {Object.entries(PRODUCT_TYPES).map(([key, value], index) => (
                    <div key={index} className="flex items-center space-x-1">
                      <input
                        type="radio"
                        id={`product-type-${key}`}
                        name="product-type"
                        value={key}
                        checked={productType === key}
                        onChange={(e) => setProductType(e.target.value)}
                      />
                      <label htmlFor={`product-type-${key}`}>{value}</label>
                    </div>
                  ))}
                </div>
              </td>
            </tr>
            <tr>
              <td>
                <label className="align-middle">Inventory / Routes:</label>
              </td>
              <td className="w-full">
                <div>
                  {ROUTES.map((option, index) => (
                    <label
                      key={index}
                      htmlFor={`route-${option}`}
                      className="mr-4 align-middle"
                    >
                      <input
                        id={`route-${option}`}
                        type="checkbox"
                        checked={routes.includes(option)}
                        onChange={() =>
                          setRoutes((prev) =>
                            prev.includes(option)
                              ? prev.filter((x) => x !== option)
                              : [...prev, option],
                          )
                        }
                      />{" "}
                      {option}
                    </label>
                  ))}
                </div>
              </td>
            </tr>

            <tr>
              <td>
                <label>Reordering Rules:</label>
              </td>
              <td className="w-full pb-0">
                <OdooLocationsMultipleSelector
                  value={reorderingRules}
                  onSelect={setReorderingRules}
                  company={originalProduct.company}
                />
              </td>
            </tr>

            <tr>
              <td>
                <label>Sales:</label>
              </td>
              <td className="w-full">
                <input
                  type="checkbox"
                  checked={saleOk}
                  onChange={() => setSaleOk((prev) => !prev)}
                />
              </td>
            </tr>
            <tr>
              <td>
                <label>Purchase:</label>
              </td>
              <td className="w-full">
                <input
                  type="checkbox"
                  checked={purchaseOk}
                  onChange={() => setPurchaseOk((prev) => !prev)}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {purchaseOk && (
        <div className="mt-8 card">
          <h5>Purchase</h5>
          <div className="mt-2">
            <SupplierInfoView
              product={originalProduct}
              hasBomProductsNotInOdoo={hasBomProductsNotInOdoo}
              prices={prices}
              computedCost={computedCost}
            />
          </div>
        </div>
      )}

      {originalProduct.children.length > 0 && (
        <div className="mt-8 card space-y-2">
          <h5>BOM</h5>
          <div className="">
            <label className="pr-3">Quantity:</label>
            <b>{originalProduct.itemsPerSet}</b>
          </div>

          <div className=" -mx-2">
            <table className="with-line">
              <tbody>
                {originalProduct.children.map((child) => (
                  <tr key={child.id}>
                    <td className="flex items-center">
                      {child.product.odooId ? (
                        <img
                          className="mr-2"
                          style={{ height: 16 }}
                          src={odooIcon}
                          alt="odoo product"
                        />
                      ) : null}{" "}
                      [{child.product.number}] {child.product.name}
                    </td>
                    <td className="text-right">&times; {child.qty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center space-x-4 mt-4">
            <label htmlFor="createBom">Sync BOM to Odoo: </label>
            <input
              id="createBom"
              type="checkbox"
              disabled={hasBomProductsNotInOdoo}
              checked={createBom}
              onChange={() => setCreateBom((prev) => !prev)}
            />
          </div>

          {hasBomProductsNotInOdoo ? (
            <div className="text-xs mt-1 text-orange-600">
              <p>
                This BOM contains products not in Odoo. In this case, we will
                not create the BOM in odoo for this product to avoid confusion.
              </p>
              <p>
                We have this setup because for example some Acrowork products,
                we break down the BOM in odoo for cost but in Odoo we don't
                care.
              </p>
              <p>
                However, we will create the cost to Odoo for this product
                variant.
              </p>
            </div>
          ) : null}
        </div>
      )}

      <div className="mt-8">
        {originalProduct.odooId && (
          <div className="text-orange-600 font-semibold mx-1">
            <p>Product already connected to an Odoo Product.</p>
            <p>
              You can still click the button below to create another Odoo
              Product. A new odoo product will be created and linked to this
              product. Make sure this is what you want before doing so.
            </p>
          </div>
        )}

        <Button
          className="mt-8"
          onClick={submitHandler}
          loading={createOdooProductRes.loading}
          primary
        >
          Create Product in AB Odoo
        </Button>
      </div>
    </div>
  );
};

export default CreateOdooProductForm;
